import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export const SUBSCRIPTION_TIERS = {
  starter: {
    price_id: "price_1T540kLTbkzNDYHr8eaSaV6l",
    product_id: "prod_U3ALUve8V2nWkT",
    name: "Starter",
    price: 39,
  },
  professional: {
    price_id: "price_1T541BLTbkzNDYHr1A76u7Mu",
    product_id: "prod_U3ALYstxPu4Pf2",
    name: "Professional",
    price: 99,
  },
  business: {
    price_id: "price_1T541YLTbkzNDYHr8XFyIj5b",
    product_id: "prod_U3AMHrD9jFba0Y",
    name: "Business",
    price: 199,
  },
  starter_yearly: {
    price_id: "price_1T542ALTbkzNDYHrE0QHTZw7",
    product_id: "prod_U3AMGu1QCvUHy5",
    name: "Starter (Yearly)",
    price: 327.6,
  },
  professional_yearly: {
    price_id: "price_1T542XLTbkzNDYHrLBKdsged",
    product_id: "prod_U3ANbLg3xwdhzh",
    name: "Professional (Yearly)",
    price: 831.6,
  },
  business_yearly: {
    price_id: "price_1T542uLTbkzNDYHr4s2XmNj1",
    product_id: "prod_U3ANLGQ4n2yXlW",
    name: "Business (Yearly)",
    price: 1671.6,
  },
};

interface SubscriptionStatus {
  subscribed: boolean;
  product_id: string | null;
  subscription_id: string | null;
  subscription_end: string | null;
  customer_id: string | null;
}

interface Invoice {
  id: string;
  number: string | null;
  amount: number;
  currency: string;
  status: string;
  created: string;
  pdf_url: string | null;
  hosted_invoice_url: string | null;
}

interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
}

interface BillingData {
  invoices: Invoice[];
  paymentMethods: PaymentMethod[];
}

export function useSubscription() {
  const { isAuthenticated, session } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [billingData, setBillingData] = useState<BillingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [billingLoading, setBillingLoading] = useState(false);

  const checkSubscription = useCallback(async () => {
    // Check both isAuthenticated and session to prevent race conditions during logout
    if (!isAuthenticated || !session) {
      setSubscriptionStatus(null);
      setLoading(false);
      return;
    }

    try {
      // Double-check session is still valid before making the call
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession?.access_token) {
        setSubscriptionStatus(null);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke("check-subscription", {
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`,
        },
      });
      
      // Handle 401/auth errors gracefully - user session may have expired server-side
      if (error) {
        const errorMessage = error.message || '';
        if (errorMessage.includes('401') || errorMessage.includes('UNAUTHORIZED') || errorMessage.includes('non-2xx')) {
          console.warn("Subscription check: session expired or unauthorized, skipping");
          setSubscriptionStatus(null);
          setLoading(false);
          return;
        }
        throw error;
      }
      
      if (data?.error === 'UNAUTHORIZED') {
        setSubscriptionStatus(null);
        setLoading(false);
        return;
      }
      
      setSubscriptionStatus(data);
    } catch (error) {
      console.error("Error checking subscription:", error);
      setSubscriptionStatus(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, session]);

  const fetchBillingHistory = useCallback(async () => {
    if (!isAuthenticated) return;

    setBillingLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("billing-history");
      if (error) throw error;
      setBillingData(data);
    } catch (error) {
      console.error("Error fetching billing history:", error);
    } finally {
      setBillingLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  const createCheckout = async (priceId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId },
      });
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      throw error;
    }
  };

  const openCustomerPortal = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Error opening customer portal:", error);
      throw error;
    }
  };

  const getCurrentTier = () => {
    if (!subscriptionStatus?.product_id) return null;
    const tier = Object.entries(SUBSCRIPTION_TIERS).find(
      ([_, value]) => value.product_id === subscriptionStatus.product_id
    );
    return tier ? tier[0] : null;
  };

  return {
    subscriptionStatus,
    billingData,
    loading,
    billingLoading,
    checkSubscription,
    fetchBillingHistory,
    createCheckout,
    openCustomerPortal,
    getCurrentTier,
    isSubscribed: subscriptionStatus?.subscribed ?? false,
  };
}
